import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { CSSObject } from '@emotion/react';
import { injectTestId } from '@cvent/nucleus-test-automation';
import {
  EventInfo,
  Get_Event_Features_And_SpeakersQuery as GetEventFeaturesAndSpeakersQuery
} from '@graphql/gql/graphql';
import { appConfigsVar } from '@metrics/client/MetricsProvider';
import { usePageActions } from '@cvent/sli-nextjs-metrics';
import { useRouter } from 'next/router';
import useBlockTheme from '@cvent/blocks/hooks/useTheme';
import { useLazyQuery } from '@apollo/client';
import { GET_POST_REG_REDIRECT_URL } from '@graphql/operations/postRegRedirectUrl/getPostRegRedirectUrl';
import { usePageActionsApi } from '@metrics/client/usePageActionsApi';
import { getRudderAnalytics } from '@metrics/rudderAnalytics';
import { ACCEPT_ALL, useCookieConsentVar } from '@context/CookieConsentProvider';
import { getRegistrationUrl } from './landingUtils';
import type { RegInfo } from './LandingPageContent';

interface RegistrationFormProps extends Pick<EventInfo, 'speakers'> {
  eventInfo: GetEventFeaturesAndSpeakersQuery['eventInfo'];
  embeddedRegistrationUrl: string;
  environment?: string;
  iframeKey: number;
  regInfo: RegInfo;
  setRegInfo: (value: RegInfo) => void;
  setConfirmationModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setRegFormLoaded: (loaded: boolean) => void;
  regFormLoaded: boolean;
  reflexEmbeddedRegUrl?: string;
  acceptAllCookies: boolean;
}

const DEFAULT_IFRAME_HEIGHT = 694;
const MINIMUM_IFRAME_HEIGHT = 455;
const REGISTRATION_LOADED = 'registrationLoaded';
const IFRAME_PADDING = 80; // top and bottom each have 40px padding
const COOKIE_CONSENT_MESSAGE = 'cookieConsentChoice';
const DEFAULT_COOKIE_CONSENT = {
  Performance: false,
  Functionality: false,
  Marketing: false
};

const useStyles = (iframeHeight: number, regFormLoaded: boolean): Record<string, CSSObject> => {
  const { block, size } = useBlockTheme();

  return {
    iframeStyle: {
      // avoid flickering as reg form loads by hiding it until it is fully loaded
      opacity: regFormLoaded ? 100 : 0,
      width: '100%',
      height: '100%',
      borderRadius: size.border.radius.fiveX,
      background: block.container,
      top: 0,
      borderWidth: 0,
      paddingLeft: '24px',
      paddingRight: '24px'
    },
    iframeContainerStyle: {
      width: '100%',
      height: iframeHeight,
      transition: 'height 0.25s ease-in',
      transitionDelay: '.1s',
      borderRadius: size.border.radius.fiveX,
      background: block.container,
      borderWidth: size.border.base,
      borderColor: block.container
    }
  };
};

function RegistrationForm({
  eventInfo,
  embeddedRegistrationUrl,
  environment,
  iframeKey,
  regInfo,
  setRegInfo,
  setConfirmationModalOpen,
  setRegFormLoaded,
  regFormLoaded,
  reflexEmbeddedRegUrl,
  acceptAllCookies
}: RegistrationFormProps): JSX.Element {
  const [iframeHeight, setIframeHeight] = useState(DEFAULT_IFRAME_HEIGHT);
  const { iframeStyle, iframeContainerStyle } = useStyles(iframeHeight, regFormLoaded);
  const { query, push } = useRouter();

  const registrationUrl = getRegistrationUrl(
    embeddedRegistrationUrl,
    eventInfo.id,
    environment,
    query,
    true,
    reflexEmbeddedRegUrl
  );
  const [getPostRegRedirectUrl] = useLazyQuery(GET_POST_REG_REDIRECT_URL, {
    context: { headers: { environment } }
  });

  const { trackAction } = usePageActions();
  const { endActWebinarAttendeeLandingRegistration } = usePageActionsApi();
  const { metricsInitialized } = appConfigsVar();
  const regRef = useRef<HTMLIFrameElement>();
  const { cookieConsent: consentChoices } = useCookieConsentVar();

  useEffect(() => {
    if (!regInfo.email) {
      return undefined;
    }
    if (eventInfo.isOpenAccessEnabled) {
      getPostRegRedirectUrl({
        variables: {
          eventId: eventInfo.id,
          attendeeEmail: regInfo.email,
          attendeeId: regInfo.attendeeId
        }
      }).then(res => {
        const result = res.data?.postRegRedirectUrl;
        if (result?.redirect) {
          const { id, verificationId } = result.postRegRedirectUrl;
          push(`/events/${eventInfo.id}?magicTokenId=${id}&magicTokenVerificationId=${verificationId}`);
        } else {
          setConfirmationModalOpen(true);
        }
      });
    } else {
      setConfirmationModalOpen(true);
    }
  }, [regInfo, eventInfo.id, eventInfo.isOpenAccessEnabled, getPostRegRedirectUrl, push, setConfirmationModalOpen]);

  const onMessage = useCallback(
    async event => {
      if (event.origin === new URL(registrationUrl).origin) {
        if (event.data?.message === 'registrationCompleted') {
          setRegInfo({
            firstName: event.data?.firstName,
            lastName: event.data?.lastName,
            email: event.data?.email,
            attendeeId: event.data?.attendeeId
          });
          trackAction('Submitted Registration Form', { eventName: eventInfo.title });
          if (event.data.email && metricsInitialized) {
            // TODO: [WEBINAR-4935] Create alias using RS SDK
          }
        } else if (event.data?.message === REGISTRATION_LOADED) {
          if (!regFormLoaded) {
            const rudderAnalytics = getRudderAnalytics();
            const anonymousId = rudderAnalytics && rudderAnalytics?.getAnonymousId();
            // only fire once if the reg form is not loaded
            endActWebinarAttendeeLandingRegistration({
              anonymousId,
              eventName: eventInfo.title
            });

            // set the default cookie consent preference
            regRef.current.contentWindow.postMessage(
              {
                message: COOKIE_CONSENT_MESSAGE,
                choice: DEFAULT_COOKIE_CONSENT
              },
              new URL(registrationUrl).origin
            );
          }
          setRegFormLoaded(true);
          if (Number.isNaN(event.data?.height)) {
            return;
          }
          // Event data has smaller than eventually required height on initial load, do not resize until > min reg size
          const eventHeight = parseInt(event.data.height, 10);
          if (eventHeight > MINIMUM_IFRAME_HEIGHT) {
            setIframeHeight(IFRAME_PADDING + eventHeight);
          }
        }
      }
    },
    [
      registrationUrl,
      trackAction,
      eventInfo.title,
      metricsInitialized,
      setRegInfo,
      setRegFormLoaded,
      regFormLoaded,
      endActWebinarAttendeeLandingRegistration
    ]
  );

  useEffect(() => {
    window.addEventListener('message', onMessage);
    return (): void => {
      window.removeEventListener('message', onMessage);
    };
  }, [onMessage]);

  useEffect(() => {
    const iframe = regRef.current;
    if (acceptAllCookies) {
      iframe.contentWindow.postMessage(
        {
          message: COOKIE_CONSENT_MESSAGE,
          choice: ACCEPT_ALL
        },
        new URL(registrationUrl).origin
      );
      return;
    }

    if (iframe && regFormLoaded && registrationUrl && consentChoices) {
      iframe.contentWindow.postMessage(
        {
          message: COOKIE_CONSENT_MESSAGE,
          choice: {
            Performance: consentChoices.Performance,
            Functionality: consentChoices.Functionality,
            Marketing: consentChoices.Marketing
          }
        },
        new URL(registrationUrl).origin
      );
    }
  }, [regFormLoaded, consentChoices, registrationUrl, acceptAllCookies]);

  return (
    <Fragment key={iframeKey}>
      <div css={iframeContainerStyle}>
        <iframe
          id="event-registration-modal-registration-form"
          title={eventInfo.title}
          {...injectTestId('event-registration-modal-registration-form')}
          css={iframeStyle}
          src={registrationUrl}
          tabIndex={0}
          ref={regRef}
        />
      </div>
    </Fragment>
  );
}

export default RegistrationForm;
